import { serverTimestamp } from 'firebase/firestore';
import Router from 'next/router';
import { contains, pathOr, values } from 'ramda';
import { ofType } from 'redux-observable';
import * as R from 'remeda';
import { concat, from, type Observable, of, timer } from 'rxjs';
import {
  auditTime,
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import uuid from 'uuid-random';
import { Weddings } from '@bridebook/models';
import type { IGroup } from '@bridebook/models/source/models/Countries/Groups.types';
import type { ITask } from '@bridebook/models/source/models/Countries/Tasks.types';
import { PremiumTiers, slugify } from '@bridebook/toolbox/src';
import type { IDatePicker } from '@bridebook/toolbox/src/datepicker/types';
import gazetteer, { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { fetchLocationData } from '@bridebook/web/app-shared/lib/search/utils';
import { toggleSnackbar } from '@bridebook/web/lib/bbcommon/actions';
import {
  ChecklistActionTypes,
  type IAddTaskAction,
  type IChangeTaskPeriodAction,
  type IDeleteCustomTaskByIdAction,
  type IDeleteTaskAction,
  type IDeleteTaskGroupAction,
  type IRenameTaskByIdAction,
  type ISetTaskGroupByIdAction,
  type ISaveTaskAssigneeAction,
  type ISetTaskDoneByIdAction,
  type IUpdateSubtaskAction,
  type IUpdateSubtasksSuccessAction,
} from '@bridebook/web/lib/checklist/action-types';
import {
  clearSubtask,
  reInitialiseChecklist,
  taskWriteError,
  deleteTask,
  deleteTaskGroup,
  taskWriteSuccess,
  updateGroup,
  updateSubtasks,
} from '@bridebook/web/lib/checklist/actions';
import {
  changedMainTaskPeriodAnalytics,
  updatedSubtaskAnalytics,
} from '@bridebook/web/lib/checklist/analytics/actions';
import { getDoneGroup } from '@bridebook/web/lib/checklist/selectors';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import { getCountryCodeWithFallback } from '@bridebook/web/lib/i18n/selectors';
import { fetchSearchPromise } from '@bridebook/web/lib/search/actions';
import { TrackingEvent } from '@bridebook/web/lib/track-utils/tracking-event';
import type { Action, IApplicationState, IEpicDeps } from '@bridebook/web/lib/types';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { getLocationName, isSameSexWedding } from '@bridebook/web/lib/weddings/selectors';
import { isAgentAction } from '@bridebook/web/lib/ai-planner/client-actions/agent-dispatch';
import { inheritActionMeta } from '@bridebook/web/lib/epics/inherit-action-meta';
import { appError } from '../app/actions';
import { initialiseChecklistTasks } from './initialise-tasks';
import type { IChecklistGroup } from './types';
import {
  changeTaskPeriodSuccess,
  closeSubtasksView,
  confirmDeleteMainTaskAnalytics,
  confirmDeleteSubtaskAnalytics,
  fetchRelatedSuppliersSuccess,
  initialiseChecklistAnalytics,
  updateSubtasksSuccess,
  updateTaskSuccess,
} from './actions';
import { getNextOrder, getNextSubtaskOrder, resolveTaskPeriod } from './utils';
import { ChecklistAnalyticsActionTypes } from '@bridebook/web/lib/checklist/analytics/action-types';

const CHECKLIST_INIT_TIMEOUT_MS = 15_000;
const CHECKLIST_INIT_TIMEOUT_ERROR =
  'Checklist initialisation timed out while waiting for listener update.';

type Trigger = { type: string; meta?: Record<string, unknown> };

// Answers a request the checklist cannot apply, so the AI planner can say why.
const refuse = (trigger: Trigger, message: string) =>
  of(taskWriteError(message)).pipe(inheritActionMeta(trigger));

// A failed write is reported back as well as logged.
const writeFailed = (error: Error) =>
  of(taskWriteError(error.message), appError({ error, feature: 'Checklist' }));

const NO_TASK = 'No checklist task has this id. Look it up with findChecklistTasks.';
const NOT_STARTED = 'The couple has not started their checklist yet.';

// Why a planner period was refused: a month is too early, anything else is not a period.
const periodRefusal = (period: string) =>
  /^\d{4}-\d{2}$/.test(period)
    ? `"${period}" is before their checklist timeline. Pick a month from now until the wedding.`
    : `"${period}" is not a period in their checklist.`;

/**
 * Ticks a checklist task or a whole group addressed by id (the AI planner
 * dispatches this raw). The id is checked against the store first, since a
 * blind `set({ done })` on an invented id would create a junk Firestore task.
 * A main task or group then runs the update its checkbox runs, and a subtask
 * the subtask update, so the UI's analytics and listeners (venue confirmation)
 * fire, with the planner's meta carried through to the write.
 */
export const setTaskDoneByIdEpic = (
  action$: Observable<ISetTaskDoneByIdAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(ChecklistActionTypes.SET_TASK_DONE_BY_ID),
    withLatestFrom(state$),
    mergeMap(([trigger, state]) => {
      const { taskId, done } = trigger.payload;
      const tasks = state.checklist.tasks ?? {};
      const task = tasks[taskId];

      // A subtask ticks on its own.
      if (task && task.group !== taskId) {
        return of(
          updateSubtasks([{ taskId, checked: done, name: task.name, method: 'active' }]),
        ).pipe(inheritActionMeta(trigger));
      }

      // A custom main task, or a Bridebook group by its id, ticks as a whole.
      const isGroup = values(tasks).some((t) => t.group === taskId);
      if (!isGroup) return refuse(trigger, NO_TASK);
      const group = state.app.groups?.[taskId] ?? ({ id: taskId, name: task?.name } as IGroup);
      return of(updateGroup(group, done, group.name ?? '')).pipe(inheritActionMeta(trigger));
    }),
  );

export const updateGroupEpic = (
  action$: Observable<Action>,
  { state$, cordovaTracker }: IEpicDeps,
) =>
  action$.pipe(
    ofType(ChecklistActionTypes.UPDATE_TASK_START),
    withLatestFrom(state$),
    mergeMap(([trigger, state]) => {
      const { task, checked, updatedMainTasksMethod, taskName } = trigger.payload;
      const {
        weddings: { profile },
        checklist: { tasks },
      } = state;
      const weddingTasks = Weddings._.getById(profile.id).Tasks;

      const { id: groupId } = task;
      const taskIds = values(tasks)
        .filter((t) => t.group === groupId)
        .map((t) => t.id);

      const getPromise = async () => {
        weddingTasks.begin();

        for (const id of taskIds) {
          await weddingTasks.getById(id).set({ done: checked });
        }

        return await weddingTasks.commit();
      };

      return from(getPromise()).pipe(
        tap(() =>
          cordovaTracker.track(TrackingEvent.UpdatedMainTasks, {
            taskId: groupId,
          }),
        ),
        mergeMap(() => [
          updateTaskSuccess({ task, checked }),

          {
            type: ChecklistAnalyticsActionTypes.UPDATE_TASK_ANALYTICS,
            payload: {
              updatedMainTasksMethod,
              checked,
              taskId: groupId,
              taskName,
            },
          },
          clearSubtask(),
        ]),
        catchError(writeFailed),
        // A planner tick waits for this result.
        inheritActionMeta(trigger),
      );
    }),
  );

export const updateTasksEpic = (
  action$: Observable<IUpdateSubtaskAction>,
  { state$, cordovaTracker }: IEpicDeps,
) =>
  action$.pipe(
    ofType(ChecklistActionTypes.UPDATE_SUBTASKS),
    withLatestFrom(state$),
    mergeMap(([trigger, state]) => {
      const { tasks } = trigger.payload;
      const {
        weddings: { profile },
      } = state;
      const weddingTasks = Weddings._.getById(profile.id).Tasks;

      const getPromise = async () => {
        weddingTasks.begin();

        for (const { taskId, checked } of tasks) {
          await weddingTasks.getById(taskId).set({ done: checked });
        }

        return await weddingTasks.commit();
      };

      const method = tasks[0].method;
      tasks.forEach(() => cordovaTracker.track(TrackingEvent.UpdatedSubtasks));
      const analytics = tasks.map(({ checked, method: taskMethod, name, taskId }) =>
        updatedSubtaskAnalytics({ subtaskId: taskId, name, checked, method: taskMethod }),
      );

      // A planner tick waits for the result, so everything carries its meta.
      return concat(
        of(...analytics),
        from(getPromise()).pipe(
          mergeMap(() => of(updateSubtasksSuccess({ method }))),
          catchError(writeFailed),
        ),
      ).pipe(inheritActionMeta(trigger));
    }),
  );

export const closeTaskDrawerEpic = (
  action$: Observable<IUpdateSubtasksSuccessAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(ChecklistActionTypes.UPDATE_SUBTASKS_SUCCESS),
    // Only run epic for non-automatic actions
    filter(({ payload: { method } }) => method === 'active'),
    withLatestFrom(state$),
    mergeMap(([, state]) => {
      const isCompleted = getDoneGroup(state);

      if (isCompleted) {
        setTimeout(() => {
          Router.push(UrlHelper.checklist.main);
        }, 1000);
      }

      return isCompleted ? of(clearSubtask()) : of();
    }),
  );

/**
 * Keeps a planner task in a period the checklist offers, turning a calendar month
 * into its period. UI periods pass as they are: they come from the checklist's
 * own sections and dropdowns, which can include overdue periods or a list built
 * before a month rollover.
 */
const toTaskPeriod = (trigger: Trigger, period: string, state: IApplicationState) => {
  if (!isAgentAction(trigger)) return period;
  const { date, l10n, tasks } = state.weddings.profile;
  return resolveTaskPeriod(
    period,
    date as IDatePicker,
    gazetteer.getMarketByCountry(l10n.country),
    tasks?.initializedAt,
  );
};

export const addTaskEpic = (action$: Observable<IAddTaskAction>, { state$ }: IEpicDeps) =>
  action$.pipe(
    ofType(ChecklistActionTypes.ADD_TASK),
    withLatestFrom(state$),
    mergeMap(([trigger, state]) => {
      const { groupId, period, name } = trigger.payload;
      const {
        weddings: { profile },
        checklist: { tasksInitial, tasks },
      } = state;
      const {
        date,
        tasks: { initializedAt },
      } = profile;
      if (!initializedAt) return refuse(trigger, NOT_STARTED);
      // Subtasks go into Bridebook groups only; a custom main task heads its own group.
      if (groupId && tasks[groupId]?.custom) {
        return refuse(trigger, 'Custom tasks take no subtasks. Add it as a main task instead.');
      }
      // A subtask takes its group's period; an unknown group or period would orphan it.
      const taskPeriod = groupId
        ? values(tasks).find((t) => t.group === groupId)?.period
        : toTaskPeriod(trigger, period, state);
      if (!taskPeriod) {
        return refuse(trigger, groupId ? 'No checklist group has this id.' : periodRefusal(period));
      }
      const market = gazetteer.getMarketByCountry(profile.l10n.country);
      const wedding = Weddings._.getById(profile.id);
      const id = uuid();
      const newTask = {
        createdAt: serverTimestamp(),
        custom: true,
        done: false,
        group: groupId || id,
        id,
        name,
        order: groupId
          ? getNextSubtaskOrder(groupId, tasks)
          : getNextOrder(
              taskPeriod,
              tasks,
              tasksInitial,
              market,
              date as IDatePicker,
              initializedAt,
            ),
        period: taskPeriod,
      };

      const pTask = wedding.Tasks.getById(id).set(newTask);

      const analyticsAction = {
        type: groupId
          ? ChecklistAnalyticsActionTypes.ADD_SUBTASK_ANALYTICS
          : ChecklistAnalyticsActionTypes.ADD_TASK_ANALYTICS,
        payload: { newTask, name, taskId: id, taskName: name },
      };

      return from(pTask).pipe(
        mergeMap(() =>
          of(
            taskWriteSuccess({ taskId: id }),
            { type: 'ADD_TASK_SUCCESS' },
            analyticsAction,
            toggleSnackbar('success', getI18n().t('checklist:snackbar.newTaskAdded')),
          ),
        ),
        catchError(writeFailed),
        inheritActionMeta(trigger),
      );
    }),
  );

export const updateTasksTotalsOnWeddingEpic = (
  action$: Observable<Action>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(ChecklistActionTypes.FETCH_USER_CHECKLIST_SUCCESS),
    withLatestFrom(state$),
    auditTime(1500),
    mergeMap(([{ payload }, state]) => {
      const {
        weddings: { profile },
      } = state;
      if (!payload) return of();
      const { result, source } = payload;
      if (source !== 'server') return of();
      const weddingId = profile.id;
      const wedding = Weddings._.getById(weddingId);
      const currentTotal = pathOr(180, ['tasks', 'total'], profile);
      const currentDone = pathOr(0, ['tasks', 'done'], profile);
      const total = result ? values(result).length : 180;
      const done = result ? values(result).filter((t) => t.done).length : 0;
      if (currentTotal === total && currentDone === done) return of();

      const pTask = wedding.set({ tasks: { done, total } });

      return from(pTask).pipe(
        mergeMap(() => of({ type: 'UPDATED_CHECKLIST_TOTALS', payload: { done, total } })),
        catchError((error) => of(appError({ error, feature: 'Checklist' }))),
      );
    }),
  );

export const assignTaskEpic = (
  action$: Observable<ISaveTaskAssigneeAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(ChecklistActionTypes.SAVE_TASK_ASSIGNEE),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { taskId, assignee },
        },
        state,
      ]) => {
        const {
          weddings: { profile },
          checklist: { tasks },
        } = state;
        const weddingTasks = Weddings._.getById(profile.id).Tasks;
        const taskIds = values(tasks)
          .filter((t) => t.group === taskId)
          .map((t) => t.id);

        const getPromise = async () => {
          weddingTasks.begin();

          for (const id of taskIds) {
            await weddingTasks.getById(id).set({ assignee });
          }

          return await weddingTasks.commit();
        };

        return from(getPromise()).pipe(
          mergeMap(() =>
            of({
              type: ChecklistActionTypes.SAVE_TASK_ASSIGNEE_SUCCESS,
            }),
          ),
          catchError((error) => of(appError({ error, feature: 'Checklist' }))),
        );
      },
    ),
  );

export const deleteGroupEpic = (
  action$: Observable<IDeleteTaskGroupAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(ChecklistActionTypes.DELETE_TASK_GROUP),
    withLatestFrom(state$),
    mergeMap(([trigger, state]) => {
      const {
        weddings: { profile },
        checklist: { groups, tasks, detailsTaskId },
      } = state;
      const { groupId } = trigger.payload;
      // The planner deletes only custom main tasks; the checklist UI can delete any group.
      const mainTask = tasks[groupId];
      if (isAgentAction(trigger) && !(mainTask?.custom && mainTask.group === groupId)) {
        return refuse(
          trigger,
          mainTask?.custom
            ? 'This is a subtask. Delete it on its own instead.'
            : 'Only main tasks the couple added can be deleted.',
        );
      }
      const taskIds = values(tasks)
        .filter((t) => t.group === groupId)
        .map((t) => t.id);
      if (!taskIds.length) return refuse(trigger, NO_TASK);

      const weddingTasks = Weddings._.getById(profile.id).Tasks;
      const getPromise = async () => {
        weddingTasks.begin();
        for (const id of taskIds) {
          await weddingTasks.getById(id).delete();
        }
        return await weddingTasks.commit();
      };

      const analytics = confirmDeleteMainTaskAnalytics(
        groups[groupId] ??
          ({
            id: groupId,
            name: mainTask?.name ?? state.app.groups?.[groupId]?.name ?? '',
          } as IChecklistGroup),
      );
      return from(getPromise()).pipe(
        mergeMap(() =>
          // Leave the task's page only when it is the one open.
          detailsTaskId === groupId
            ? of(taskWriteSuccess(), analytics, closeSubtasksView())
            : of(taskWriteSuccess(), analytics),
        ),
        catchError(writeFailed),
        inheritActionMeta(trigger),
      );
    }),
  );

/**
 * Deletes a custom task for the planner, which cannot always tell a main task from a
 * subtask: a main task goes with its whole group, a subtask on its own. Forwarding to
 * the checklist's own deletes keeps one approval and their analytics, with the planner's
 * meta carried through to the result.
 */
export const deleteCustomTaskByIdEpic = (
  action$: Observable<IDeleteCustomTaskByIdAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(ChecklistActionTypes.DELETE_CUSTOM_TASK_BY_ID),
    withLatestFrom(state$),
    mergeMap(([trigger, state]) => {
      const { taskId } = trigger.payload;
      const task = state.checklist.tasks[taskId];
      if (!task) return refuse(trigger, NO_TASK);
      if (!task.custom) return refuse(trigger, 'Only tasks the couple added can be deleted.');
      const forwarded =
        task.group === taskId ? deleteTaskGroup({ groupId: taskId }) : deleteTask({ taskId });
      return of(forwarded).pipe(inheritActionMeta(trigger));
    }),
  );

export const deleteTaskEpic = (action$: Observable<IDeleteTaskAction>, { state$ }: IEpicDeps) =>
  action$.pipe(
    ofType(ChecklistActionTypes.DELETE_TASK),
    withLatestFrom(state$),
    mergeMap(([trigger, state]) => {
      const { taskId } = trigger.payload;
      const task = state.checklist.tasks[taskId];
      if (!task) return refuse(trigger, NO_TASK);
      // The planner deletes only custom subtasks; a main task goes with its group.
      if (isAgentAction(trigger) && (!task.custom || task.group === taskId)) {
        return refuse(
          trigger,
          task.custom
            ? 'This is a main task. Delete it with its group instead.'
            : 'Only tasks the couple added can be deleted.',
        );
      }

      const pDelete = Weddings._.getById(state.weddings.profile.id).Tasks.getById(taskId).delete();
      return from(pDelete).pipe(
        mergeMap(() => of(taskWriteSuccess(), confirmDeleteSubtaskAnalytics(task))),
        catchError(writeFailed),
        inheritActionMeta(trigger),
      );
    }),
  );

// Renames a custom task by id. Bridebook tasks keep their template names, so they are left alone.
export const renameTaskByIdEpic = (
  action$: Observable<IRenameTaskByIdAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(ChecklistActionTypes.RENAME_TASK_BY_ID),
    withLatestFrom(state$),
    mergeMap(([trigger, state]) => {
      const { taskId, name } = trigger.payload;
      const task = state.checklist.tasks[taskId];
      if (!task) return refuse(trigger, NO_TASK);
      if (!task.custom) return refuse(trigger, 'Only tasks the couple added can be renamed.');
      const pRename = Weddings._.getById(state.weddings.profile.id)
        .Tasks.getById(taskId)
        .set({ name });
      return from(pRename).pipe(
        mergeMap(() => of(taskWriteSuccess())),
        catchError(writeFailed),
        inheritActionMeta(trigger),
      );
    }),
  );

/**
 * Moves a custom task between groups: into a Bridebook group as a subtask, taking
 * its period, or out of its group as a main task in the same period. Custom
 * groups take no subtasks, and a main task with subtasks stays put, since
 * subtasks cannot have subtasks.
 */
export const setTaskGroupByIdEpic = (
  action$: Observable<ISetTaskGroupByIdAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(ChecklistActionTypes.SET_TASK_GROUP_BY_ID),
    withLatestFrom(state$),
    mergeMap(([trigger, state]) => {
      const { taskId, groupId } = trigger.payload;
      const {
        checklist: { tasks, tasksInitial },
        weddings: { profile },
      } = state;
      const task = tasks[taskId];
      const { initializedAt } = profile.tasks;
      if (!task) return refuse(trigger, NO_TASK);
      if (!task.custom) return refuse(trigger, 'Only tasks the couple added can be moved.');
      if (!initializedAt) return refuse(trigger, NOT_STARTED);

      let update: { group: string; period: string; order: number };
      if (groupId === null) {
        if (task.group === taskId) return refuse(trigger, 'This is already a main task.');
        update = {
          group: taskId,
          period: task.period,
          order: getNextOrder(
            task.period,
            tasks,
            tasksInitial,
            gazetteer.getMarketByCountry(profile.l10n.country),
            profile.date as IDatePicker,
            initializedAt,
          ),
        };
      } else {
        const target = values(tasks).find((t) => t.group === groupId && t.id !== taskId);
        const hasSubtasks = values(tasks).some((t) => t.group === taskId && t.id !== taskId);
        if (tasks[groupId]?.custom) {
          return refuse(trigger, 'Custom tasks take no subtasks. Pick a Bridebook group.');
        }
        if (!target) return refuse(trigger, 'No checklist group has this id.');
        if (task.group === groupId) return refuse(trigger, 'The task is already in this group.');
        if (hasSubtasks) return refuse(trigger, 'A main task with subtasks cannot join a group.');
        update = {
          group: groupId,
          period: target.period,
          order: getNextSubtaskOrder(groupId, tasks),
        };
      }

      const pMove = Weddings._.getById(profile.id).Tasks.getById(taskId).set(update);
      return from(pMove).pipe(
        mergeMap(() => of(taskWriteSuccess())),
        catchError(writeFailed),
        inheritActionMeta(trigger),
      );
    }),
  );

export const initialiseChecklistEpic = (
  action$: Observable<Action>,
  { state$, cordovaTracker }: IEpicDeps,
) =>
  action$.pipe(
    ofType(ChecklistActionTypes.INITIALISE_CHECKLIST),
    withLatestFrom(state$),
    exhaustMap(([action, state]) => {
      const {
        profile: { date, id, l10n, tasks, roles },
      } = state.weddings;
      if (!date) {
        return of(reInitialiseChecklist(false));
      }
      const isSameSexRoles = isSameSexWedding(state);
      const { initial = true }: { initial: boolean } = action.payload;

      return from(
        initialiseChecklistTasks({
          weddingId: id,
          date,
          country: l10n.country,
          initializedAt: tasks.initializedAt,
          roles,
          isSameSexRoles,
          initial,
        }),
      ).pipe(
        mergeMap(({ persisted }) => {
          if (!persisted) {
            // Nothing changed in Firestore, so stop the init flow instead of
            // firing success analytics and waiting for listener confirmation
            // that can never arrive.
            return of(reInitialiseChecklist(false));
          }

          cordovaTracker.track(TrackingEvent.InitializedChecklist);

          return concat(
            of(initialiseChecklistAnalytics(initial)),
            // Wait for listener to confirm init; timeout after 15s if still reInitialising
            timer(CHECKLIST_INIT_TIMEOUT_MS).pipe(
              withLatestFrom(state$),
              filter(([, latestState]) => latestState.checklist.reInitialising),
              mergeMap(() =>
                of(
                  appError({
                    error: new Error(CHECKLIST_INIT_TIMEOUT_ERROR),
                    feature: 'Checklist',
                  }),
                  reInitialiseChecklist(false),
                ),
              ),
              takeUntil(
                action$.pipe(
                  ofType(
                    ChecklistActionTypes.FETCH_USER_CHECKLIST_SUCCESS,
                    ChecklistActionTypes.STOP_LISTENER,
                  ),
                  filter(
                    (checklistAction: Action) =>
                      checklistAction.type === ChecklistActionTypes.STOP_LISTENER ||
                      Boolean(
                        checklistAction.type ===
                          ChecklistActionTypes.FETCH_USER_CHECKLIST_SUCCESS &&
                          checklistAction.payload?.initializedAt,
                      ),
                  ),
                  take(1),
                ),
              ),
            ),
          );
        }),
        catchError((error) =>
          of(appError({ error, feature: 'Checklist' }), reInitialiseChecklist(false)),
        ),
      );
    }),
  );

export const fetchRelatedSupplierEpic = (action$: Observable<Action>, { state$ }: IEpicDeps) =>
  action$.pipe(
    ofType(ChecklistActionTypes.FETCH_RELATED_SUPPLIERS),
    withLatestFrom(state$),
    mergeMap(([action, state]: [any, IApplicationState]) => {
      const { supplierSlug } = action.payload;
      const area = getLocationName(state) || 'UK';
      const market = gazetteer.getMarketByCountry(state.weddings.profile.l10n.country);

      const minTier =
        supplierSlug === 'venues' && market.country === CountryCodes.GB
          ? PremiumTiers.Tier_1
          : undefined;

      const getPromise = async () => {
        // Fetch additional location data for search context
        const locationData = await fetchLocationData({
          area: slugify(area),
          slug: supplierSlug,
          market,
        });

        const query = {
          slug: supplierSlug,
          area,
          oldSlug: true,
          isLoggedIn: true,
          minTier,
          locationData: {
            ...locationData,
            countryCode: getCountryCodeWithFallback(state),
          },
        };

        return fetchSearchPromise(query);
      };

      return from(getPromise()).pipe(
        map(fetchRelatedSuppliersSuccess),
        catchError((error) => of(appError({ error, feature: 'Checklist' }))),
      );
    }),
  );

export const leaveChecklistEpic = (action$: Observable<Action>) =>
  action$.pipe(
    ofType('ROUTE_CHANGE_COMPLETE'),
    mergeMap(({ payload: { previousPath, serverPage } }) =>
      contains(UrlHelper.checklist.main, previousPath) &&
      // `serverPage` is the current path, and it can be either the main
      // checklist page or the subtasks page
      !contains(UrlHelper.checklist.main, serverPage)
        ? of(clearSubtask())
        : of(),
    ),
  );

export const tasksPeriodChangeEpic = (
  action$: Observable<IChangeTaskPeriodAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(ChecklistActionTypes.TASKS_PERIOD_CHANGE),
    withLatestFrom(state$),
    mergeMap(([trigger, state]) => {
      const { groupId } = trigger.payload;
      const {
        checklist: { tasks, groups, tasksInitial },
        weddings: {
          profile: {
            id: weddingId,
            date,
            tasks: { initializedAt },
          },
        },
      } = state;
      if (!initializedAt) return refuse(trigger, NOT_STARTED);
      // The planner may name a calendar month; anything the checklist does not offer moves nothing.
      const period = toTaskPeriod(trigger, trigger.payload.period, state);
      if (!period) return refuse(trigger, periodRefusal(trigger.payload.period));
      const market = gazetteer.getMarketByCountry(state.weddings.profile.l10n.country);
      const groupTasksIds = R.pipe<ITask[], ITask[], ITask[], string[]>(
        values(tasks),
        R.filter<ITask>((t) => t.group === groupId),
        // @ts-ignore FIXME
        R.sortBy(R.prop<ITask, 'order'>('order')),
        R.map<ITask, string>((t) => t.id),
      );
      if (!groupTasksIds.length) return refuse(trigger, 'No checklist group has this id.');
      const latestOrder = getNextOrder(
        period,
        tasks,
        tasksInitial,
        market,
        date as IDatePicker,
        initializedAt,
      );
      // Refused rather than thrown: a throw here would end the epic for every later move.
      if (!latestOrder) return refuse(trigger, 'Could not place the task in that period.');

      const weddingTasks = Weddings._.getById(weddingId).Tasks;

      const getPromise = async () => {
        let orderingIndex = 0;

        weddingTasks.begin();

        for (const id of groupTasksIds) {
          const order = latestOrder + (orderingIndex++ + 1) * 0.01;

          await weddingTasks.getById(id).set({
            period,
            order,
          });
        }

        return await weddingTasks.commit();
      };

      // Analytics: groups are built on the checklist page only, so fall back to the main task,
      // or the template group for a Bridebook one.
      const mainTask =
        groups[groupId] ??
        ({
          id: groupId,
          name: tasks[groupId]?.name ?? state.app.groups?.[groupId]?.name,
        } as IChecklistGroup);

      return from(getPromise()).pipe(
        mergeMap(() => of(changeTaskPeriodSuccess(), changedMainTaskPeriodAnalytics(mainTask))),
        catchError(writeFailed),
        inheritActionMeta(trigger),
      );
    }),
  );

export const pageEpics = [
  renameTaskByIdEpic,
  setTaskGroupByIdEpic,
  fetchRelatedSupplierEpic,
  leaveChecklistEpic,
  tasksPeriodChangeEpic,
  assignTaskEpic,
  deleteGroupEpic,
  deleteTaskEpic,
  deleteCustomTaskByIdEpic,
  addTaskEpic,
];
